import { useState } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import {
  STATUS_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
} from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';

export interface ILicenseFilterValues {
  status: string;
  plan: string;
  year: string;
}

type TLicenseFilterMode = 'parents' | 'schools';

interface ILicenseFilterModalProps {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: ILicenseFilterValues) => void;
  initialValues?: Partial<ILicenseFilterValues>;
  planOptions?: { value: string; label: string }[];
  yearOptions?: { value: string; label: string }[];
  mode?: TLicenseFilterMode;
}

const LicenseFilterModal = ({
  setIsOpen,
  onApply,
  initialValues = {},
  planOptions = [],
  yearOptions = [],
  mode = 'parents',
}: ILicenseFilterModalProps) => {
  const { formatText } = useFormatText();

  const [status, setStatus] = useState<string>(initialValues.status || '');
  const [plan, setPlan] = useState<string>(initialValues.plan || '');
  const [year, setYear] = useState<string>(initialValues.year || '');

  const isSchools = mode === 'schools';

  const handleClear = () => {
    setStatus('');
    setPlan('');
    setYear('');
    onApply({ status: '', plan: '', year: '' });
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply({ status, plan, year });
    setIsOpen(false);
  };

  const isDisableApply = isSchools ? !status && !year : !status && !plan;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled: isDisableApply,
        className: 'bg-purple-600 hover:bg-purple-700',
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        variant: 'link',
        onClick: handleClear,
        className: 'text-sm px-0! text-neutral-primary!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        <RadioButtonGroup
          name="status"
          label={formatText(
            isSchools
              ? 'report-analytics.license.filters.subscription-status'
              : 'report-analytics.license.table.status'
          )}
          options={isSchools ? SUBSCRIPTION_STATUS_OPTIONS : STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          formatLabel={(label) => formatText(label)}
          gap="gap-8"
        />

        {isSchools ? (
          <Select
            name={formatText(
              'report-analytics.license.filters.subscription-start-year'
            )}
            options={yearOptions}
            selected={year}
            onChange={(value) => setYear(value as string)}
            placeholder={formatText(
              'report-analytics.license.filters.subscription-start-year-placeholder'
            )}
            isMulti={false}
            isClearable={true}
            className="min-h-13"
          />
        ) : (
          <Select
            name={formatText('report-analytics.license.filters.plans')}
            options={planOptions}
            selected={plan}
            onChange={(value) => setPlan(value as string)}
            placeholder={formatText('common.select')}
            isMulti={false}
            isClearable={true}
            className="min-h-13"
            isTextFormat
          />
        )}
      </div>
    </CustomModal>
  );
};

export default LicenseFilterModal;
